import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { API_URL, LOCATION_ID, TOKEN } from "..";
import MasterLayout from "../masterLayout/MasterLayout";
import Swal from "sweetalert2";
import axios from "axios";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

/**
 * ViewCustomerPage component displays customer details, statistics, and related invoices, quotes, and jobs as simple values.
 * @returns {JSX.Element} The rendered customer view page.
 */
const ViewCustomerPage = () => {

  const  TOKEN = localStorage.getItem('tokenID')
  const { id } = useParams();
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const formatDateBefore = (date) => date.toISOString().split("T")[0];
  const [ledger, setLedger] = useState(null);

  const [fromDate, setFromDate] = useState(formatDateBefore(firstDayOfMonth));
  const [toDate, setToDate] = useState(formatDateBefore(today));
  const [assignedUser, setAssignedUser] = useState(null);

  const navigate = useNavigate();
  const [customerData, setCustomerData] = useState({
    id: "",
    dateAdded: "",
    type: "",
    locationId: "",
    firstName: "",
    lastName: "",
    fullName: "",
    email: "",
    phone: "",
    city: "",
    country: "",
    source: "",
    companyName: "",
    tags: "",
    dateUpdated: "",
    assignedTo: "",
    status: "active",
  });
  const [message, setMessage] = useState({
    status: false,
    reason: "",
    desc: "",
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCustomerFromGHL();
  }, []);

  /**
   * Fetches single customer data from GoHighLevel API using the contact ID.
   */
  const fetchCustomerFromGHL = async () => {
    setLoading(true);
    const options = {
      method: "GET",
      url: `https://services.leadconnectorhq.com/contacts/${id}`,
      params: { locationId: LOCATION_ID },
      headers: {
        Authorization: `Bearer ${TOKEN}`,
        Version: "2021-07-28",
        Accept: "application/json",
      },
    };

    try {
      const response = await axios.request(options);
      const contact = response.data.contact || response.data;
      if (contact) {
        setCustomerData({
          id: contact.id || "N/A",
          dateAdded: contact.dateAdded || "N/A",
          type: contact.type || "N/A",
          locationId: contact.locationId || "N/A",
          firstName: contact.firstName || "N/A",
          lastName: contact.lastName || "N/A",
          fullName:
            `${contact.firstName || ""} ${contact.lastName || ""}`.trim() ||
            "N/A",
          email: contact.email || "N/A",
          phone: contact.phone || "N/A",
          city: contact.city || "N/A",
          country: contact.country || "N/A",
          source: contact.source || "N/A",
          companyName: contact.companyName || "N/A",
          tags: contact.tags?.length ? contact.tags.join(", ") : "N/A",
          dateUpdated: contact.dateUpdated || "N/A",
          assignedTo: contact.assignedTo || "N/A",
          status: contact.type === "lead" ? "active" : "inactive",
        });
      } else {
        throw new Error("Contact not found");
      }
    } catch (error) {
      console.error("Error fetching contact from GoHighLevel:", error);
      setMessage({
        status: true,
        reason: "error",
        desc: `Failed to fetch customer from GoHighLevel: ${error.message}`,
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  useEffect(() => {
    fetchLedger();
  }, []);
  useEffect(() => {
    if (customerData.assignedTo) {
      fetchAssignedUser(customerData.assignedTo);
    }
  }, [customerData.assignedTo]);
  
  const fetchAssignedUser = async (userId) => {
    try {
      const response = await axios.get(
        `https://services.leadconnectorhq.com/users/${userId}`,
        {
          headers: {
            Authorization: `Bearer ${TOKEN}`,
            Version: "2021-07-28",
            Accept: "application/json",
          },
        }
      );
  
      if (response.data) {
        setAssignedUser(response.data);
      }
    } catch (error) {
      console.error("Error fetching assigned user:", error);
    }
  };
  const fetchLedger = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/payments/customer/${id}`,
        { params: { fromDate, toDate } }
      );
      setLedger(response.data);
    } catch (error) {
      console.error("Error fetching ledger:", error);
    }
  };

  const exportToExcel = () => {
    if (!ledger) return;

    const tableData = ledger.ledger.map((inv) => ({
      "Order Form #": inv.invoiceNumber,
      "Issue Date": inv.issueDate,
      "Due Date": inv.dueDate,
      "Order Form Amount": inv.invoiceAmount,
      Paid: inv.paidAmount,
      Remaining: inv.remaining,
      Payments: inv.payments
        .map(
          (p) =>
            `${inv.currency}${p.receivedAmount} via ${p.account?.type || "N/A"} (${
              p.account?.title || "N/A"
            })`
        )
        .join(", "),
    }));

    // Add footer totals
    tableData.push({
      "Order Form #": "Totals",
      "Order Form Amount": ledger.stats?.totalInvoiceAmount || 0,
      Paid: ledger.stats?.totalPaidAmount || 0,
      Remaining: ledger.stats?.totalRemainingBalance || 0,
    });

    const worksheet = XLSX.utils.json_to_sheet(tableData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Ledger");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const data = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(data, `customer_ledger_${id}.xlsx`);
  };
  const exportToPDF = () => {
    if (!ledger) return;
  
    const doc = new jsPDF();
  
    // Title
    doc.setFontSize(16);
    doc.text("Customer Ledger", 14, 20);
  
    // Build table rows
    const tableData = ledger.ledger.map((inv) => [
        inv.invoiceNumber,
        inv.issueDate,
        inv.dueDate,
        `${inv.currency}${inv.invoiceAmount.toFixed(2)}`,
        `${inv.currency}${inv.paidAmount.toFixed(2)}`,
        `${inv.currency}${inv.remaining.toFixed(2)}`,
        inv.payments.length > 0
          ? inv.payments.map(
              (p) =>
                `${inv.currency}${p.receivedAmount} via ${p.account?.type || "N/A"} (${
                  p.account?.title || "N/A"
                })`
            ) // return an array of strings
          : ["No Payments"], // also make this an array
      ]);
  
    // Add totals row
    tableData.push([
      "Totals",
      "",
      "",
      ledger?.stats?.totalInvoiceAmount?.toFixed(2) || 0,
      ledger?.stats?.totalPaidAmount?.toFixed(2) || 0,
      ledger?.stats?.totalRemainingBalance?.toFixed(2) || 0,
      "-",
    ]);
  
    // AutoTable
    autoTable(doc, {
        startY: 30,
        head: [["Order Form #", "Issue Date", "Due Date", "Amount", "Paid", "Remaining", "Payments"]],
        body: tableData,
        theme: "striped",
        headStyles: { fillColor: [41, 128, 185] },
      });
  
    // Save PDF
    doc.save(`customer_ledger_${id}.pdf`);
  };
  if (loading) {
    return (
      <MasterLayout>
        <div className="container py-5">
          <div className="text-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading customer details...</p>
          </div>
        </div>
      </MasterLayout>
    );
  }


  return (
    <MasterLayout>
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-12">
            {/* Date Filters */}
            
            <div className="d-flex mb-3">
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="form-control me-2"
              />
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="form-control me-2"
              />
              <button className="btn btn-primary" onClick={fetchLedger}>
                Filter
              </button>
            </div>
            <div className="card shadow-sm border-0 mb-4 animate__animated animate__fadeIn">
              <div className="card-header bg-white border-bottom d-flex justify-content-between align-items-center py-3">
                <p
                  style={{ fontWeight: "500", fontSize: 20 }}
                  className="mb-0 text-secondary"
                >
                  Customer Details
                </p>
              </div>
              <div className="card-body p-3">
                {message.status && (
                  <div
                    className={`alert alert-${
                      message.reason === "error" ? "danger" : "success"
                    } alert-dismissible fade show mb-4`}
                    role="alert"
                  >
                    {message.desc}
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setMessage({ ...message, status: false })}
                      aria-label="Close"
                    ></button>
                  </div>
                )}
                <div className="row g-3">
                
                 
                  <div className="col-md-6">
                    <h6 className="text-muted mb-1">Full Name</h6>
                    <p className="mb-0">{customerData.fullName}</p>
                  </div>
                  <div className="col-md-6">
                    <h6 className="text-muted mb-1">Email Address</h6>
                    <p className="mb-0">{customerData.email}</p>
                  </div>
                  <div className="col-md-6">
                    <h6 className="text-muted mb-1">Phone Number</h6>
                    <p className="mb-0">{customerData.phone}</p>
                  </div>
                  <div className="col-md-6">
                    <h6 className="text-muted mb-1">City</h6>
                    <p className="mb-0">{customerData.city}</p>
                  </div>
                  <div className="col-md-6">
                    <h6 className="text-muted mb-1">Country</h6>
                    <p className="mb-0">{customerData.country}</p>
                  </div>
                  <div className="col-md-6">
                    <h6 className="text-muted mb-1">Source</h6>
                    <p className="mb-0">{customerData.source}</p>
                  </div>
                  <div className="col-md-6">
                    <h6 className="text-muted mb-1">Company Name</h6>
                    <p className="mb-0">{customerData.companyName}</p>
                  </div>
                
                  
                  <div className="col-md-6">
                    <h6 className="text-muted mb-1">Assigned To</h6>
                    <p className="mb-0">
                        {assignedUser
                        ? `${assignedUser.firstName || ""} ${assignedUser.lastName || ""}`
                        : customerData.assignedTo || "N/A"}
                    </p>
                  </div>
                  <div className="col-md-6">
                    <h6 className="text-muted mb-1">Status</h6>
                    <p className="mb-0 text-capitalize">
                      {customerData.status}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            {/* Ledger Table */}
            <div className="card shadow-sm border-0 mb-4 animate__animated animate__fadeIn">
              <div className="card-header bg-white border-bottom d-flex justify-content-between align-items-center py-3">
                <h5 className="mb-0">Customer Ledger</h5>
                <div>
                <button
                    className="btn btn-sm btn-outline-primary me-2"
                    onClick={exportToPDF}
                    >
                    <Icon icon="mdi:printer" className="me-1" /> PDF
                    </button>
                  <button
                    className="btn btn-sm btn-outline-success"
                    onClick={exportToExcel}
                  >
                    <Icon icon="mdi:download" className="me-1" /> Excel
                  </button>
                </div>
              </div>
              <div className="card-body">
                {ledger && (
                  <div className="table-responsive">
                    <table className="table table-bordered table-striped align-middle text-center">
                      <thead className="table-primary">
                        <tr>
                          <th>Order Form #</th>
                          <th>Issue Date</th>
                          <th>Due Date</th>
                          <th>Order Form Amount</th>
                          <th>Paid</th>
                          <th>Remaining</th>
                          <th>Payments</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ledger.ledger.map((inv) => (
                          <tr key={inv.invoiceId}>
                              <td><Link className="text-primary" to={`/invoice-preview/${inv.invoiceId}`}>{inv.invoiceNumber}</Link></td>
                            <td>{inv.issueDate}</td>
                            <td>{inv.dueDate}</td>
                            <td className="fw-bold text-primary">
                              {inv.currency}{inv.invoiceAmount.toFixed(2)}
                            </td>
                            <td className="text-success fw-semibold">
                              {inv.currency}{inv.paidAmount.toFixed(2)}
                            </td>
                            <td
                              className={`fw-semibold ${
                                inv.remaining > 0
                                  ? "text-danger"
                                  : "text-muted"
                              }`}
                            >
                              {inv.currency}{inv.remaining.toFixed(2)}
                            </td>
                            <td>
                              {inv.payments.length > 0 ? (
                                <ul className="list-unstyled mb-0">
                                  {inv.payments.map((p) => (
                                    <li key={p.paymentId}>
                                      £{p.receivedAmount} via{" "}
                                      {p.account ? p.account.type : "N/A"} (
                                      {p.account?.title || "N/A"})
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <span className="badge bg-secondary">
                                  No Payments
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="table-dark fw-bold">
                        <tr>
                          <td colSpan="3" className="text-end">
                            Totals:
                          </td>
                          <td>
                            
                            {ledger?.stats?.totalInvoiceAmount?.toFixed(2) ||
                              0}
                          </td>
                          <td>
                            {ledger?.stats?.totalPaidAmount?.toFixed(2) || 0}
                          </td>
                          <td>
                            
                            {ledger?.stats?.totalRemainingBalance?.toFixed(2) ||
                              0}
                          </td>
                          <td>-</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>
            </div>

            {/* Customer Details */}
         
          </div>
        </div>
      </div>
    </MasterLayout>
  );
};

export default ViewCustomerPage;
